import React, {useEffect} from 'react'
import ReactDOM from 'react-dom'
import {Box, IconButton} from '@chakra-ui/react'
import {CloseIcon} from '@chakra-ui/icons'
import {useCustomToast} from '../../contexts/custom-toast-context'
import {useHeaderHeight} from '../../hooks/use-header-height'

const CustomToast = () => {
    const {
        showToastMessage,
        setShowToastMessage,
        toastMessage,
        toastAction,
        toastStyles,
        toastMessageStyles,
        toastContainerId,
        toastContainerRef,
        toastDuration,
        onClose
    } = useCustomToast()

    // Get header height for proper positioning on mobile
    const headerHeight = useHeaderHeight()

    useEffect(() => {
        if (showToastMessage) {
            const timer = setTimeout(() => {
                setShowToastMessage(false)
                onClose() // Call onClose when the toast is automatically dismissed
            }, toastDuration)

            return () => clearTimeout(timer)
        }
    }, [showToastMessage, toastDuration, setShowToastMessage, onClose])

    if (!showToastMessage) return null

    // Check if we're in browser environment before accessing document
    if (typeof document === 'undefined') return null

    // Priority: toastContainerRef > containerId > document.body
    // Cart toasts use containerId: 'app-header' for relative positioning
    // Wishlist toasts don't pass containerId, so they use document.body for fixed positioning
    let container = toastContainerRef
    if (!container && toastContainerId) {
        container = document.getElementById(toastContainerId)
    }
    if (!container) {
        container = document.body
    }
    if (!container) return null

    const handleClose = (e) => {
        // Prevent any default behavior and stop event propagation
        // to avoid interfering with scroll state or header visibility
        if (e) {
            e.preventDefault()
            e.stopPropagation()
        }
        setShowToastMessage(false)
        onClose() // Call onClose when the toast is manually dismissed
    }

    return ReactDOM.createPortal(
        <Box
            position="fixed"
            top={{base: `${headerHeight}px`, lg: 'auto'}}
            bottom={{base: 'auto', lg: '24px'}}
            width={{base: '100%', lg: '343px'}}
            right={{base: '0', lg: 'auto'}}
            left={{base: '0', lg: '24px'}}
            bg="black"
            color="white"
            px={3}
            py={4}
            borderColor="warmBlack"
            zIndex="9999"
            display="flex"
            justifyContent="end"
            alignItems="center"
            fontSize="sm"
            {...toastStyles}
        >
            <Box
                {...{
                    mr: 'auto',
                    fontWeight: 'medium',
                    lineHeight: '.8',
                    ...toastMessageStyles
                }}
            >
                {toastMessage}
            </Box>
            {toastAction && toastAction}
            <IconButton
                icon={<CloseIcon boxSize={3} />}
                onClick={handleClose}
                ml="6"
                aria-label="Close"
                variant="ghost"
                size="sm"
                minW="auto"
                h="auto"
                color="white"
                _hover={{bg: 'transparent'}}
                _active={{bg: 'transparent'}}
            />
        </Box>,
        container
    )
}

export default CustomToast
